import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import IDENTITY from 'lodash/identity';
import NOOP from 'lodash/noop';

const isAndroid = /Android/i.test(typeof navigator !== 'undefined' && navigator.userAgent);
const SELECTABLE_TYPES = /text|password|search|tel|url/;

class Control extends PureComponent {
  static propTypes = {
    name: PropTypes.string,
    id: PropTypes.string,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    value: PropTypes.any,
    valid: PropTypes.bool,
    active: PropTypes.bool,
    touched: PropTypes.bool,
    dirty: PropTypes.bool,
    error: PropTypes.any,
    parse: PropTypes.func,
    format: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    //onMouseEnter: PropTypes.func,
    //onMouseLeave: PropTypes.func
  };

  static defaultProps = {
    parse: IDENTITY,
    format: IDENTITY,
    onChange: IDENTITY,
    onMouseEnter: NOOP,
    onMouseLeave: NOOP,
    onFocus: NOOP,
    onBlur: NOOP,
    valid: true,
    active: false,
    touched: false,
    dirty: false,
  };

  constructor(props, ctx) {
    super(props, ctx);

    this.handleChange = this.handleChange.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    //this.handleMouseEnter = this.handleMouseEnter.bind(this);
    //this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handlePaste = this.handlePaste.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleClick = this.handleClick.bind(this);

    this.state = {
      focused: false,
      touched: false,
      hovered: false,
    };

    const initialValue = typeof props.initialValue === 'string' ? props.initialValue : props.value;
    this.state.initialValue = this.format(initialValue).value;
    this.state.prevValue = this.state.initialValue;
    this.state.empty = !this.state.initialValue;
  }

  parse() {
    return this.props.parse(...arguments, this.state);
  }

  format(value, prev, cursor) {
    const result = this.props.format.call(this.props, value, prev, cursor, this.state);
    if (this.props.format.isFormatter) {
      return result;
    } else {
      return {
        value: result,
        cursor: cursor,
      };
    }
  }

  getValueFromEvent(e) {
    let value;
    if (typeof e === 'object' && e.target) {
      value = e.target.value;
    } else {
      value = e;
    }

    return this.parse(value);
  }

  handleChange(e, ...rest) {
    const { input } = this.refs;

    const { ignoreCursor, format, onChange } = this.props;

    const supportSelection = SELECTABLE_TYPES.test(e.target.type);

    let value = '';

    if (input && supportSelection) {
      const inputValue = input.value;
      const { prevValue } = this.state;
      const cursor = input.selectionEnd || 0;

      if (format.isFormatter) {
        value = inputValue;
      } else {
        value = this.getValueFromEvent(e);
      }

      const result = this.format(value, prevValue, cursor);

      value = result.value;

      if (value != inputValue) {
        input.value = value;
      }

      if (!ignoreCursor) {
        // если после изменения символов строка сформатиловалась как и была
        // то просто передвигаем курсор на кол-во измененных символов
        const lengthDiff = inputValue.length - result.value.length;

        if (lengthDiff < 0) {
          if (result.cursor && prevValue === result.value) {
            result.cursor += lengthDiff;
          }
        } else if (lengthDiff > 0) {
          if (!result.cursor && prevValue === result.value) {
            result.cursor -= lengthDiff;
          }
        }

        if (value != inputValue || cursor != result.cursor) {
          if (isAndroid) {
            setTimeout(() => {
              input.selectionStart = result.cursor;
              input.selectionEnd = result.cursorPosition;
            });
          } else {
            input.selectionStart = result.cursor;
            input.selectionEnd = result.cursorPosition;
          }
        }
      }
    } else {
      value = this.getValueFromEvent(e);
      value = this.format(value).value; // fixme remove?
    }

    this.setState({
      touched: true,
      empty: !this.hasValue(),
      prevValue: value,
    });

    onChange(this.parse(value), ...arguments);
  }

  handleFocus() {
    this.setState(
      {
        focused: true,
      },
      () => this.props.onFocus(...arguments)
    );
  }

  handleBlur(e) {
    const valueFromEvent = this.getValueFromEvent(e);

    this.setState({
      focused: false,
    });

    this.props.onBlur(valueFromEvent, ...arguments);
  }

  hasValue() {
    if (this.props.value != null) {
      return Boolean(this.props.value);
    }
    return Boolean(this.refs.input && this.refs.input.value);
  }

  // Надо удалить, когда у всех наследников выпилим.
  getContainerProps() {
    return {
      //onMouseEnter: this.handleMouseEnter,
      //onMouseLeave: this.handleMouseLeave
    };
  }

  getModificators() {
    const { touched, focused, initialValue, empty } = this.state;
    const {
      value,
      valid,
      active,
      touched: propsTouched,
      dirty,
      theme,
      isWithSwapDirectionsButton,
    } = this.props;

    const isInitial = value === initialValue;
    const modificators = {
      isTouched: touched,
      isFocused: focused,
      isValid: valid,
      isPristine: isInitial,
      isDirty: touched && !isInitial,
      hasError: !valid,
      isError: !valid && !active && (propsTouched || dirty),
      isNewError: !valid && (propsTouched || dirty),
      hasValue: !!this.hasValue(),
      isEmpty: empty,
      theme,
      isWithSwapDirectionsButton,
    };

    return modificators;
  }

  getLabelText() {
    return this.props.label || this.props.placeholder || this.props.name;
  }

  handlePaste() {}

  handleKeyPress() {}

  handleKeyDown(event) {
    this.props.onKeyDown && this.props.onKeyDown(event);
  }

  handleClick(event) {
    this.props.onClick && this.props.onClick(event);
  }

  getInputProps() {
    const { props, state } = this;

    const dataAttributes = Object.keys(props).reduce((acc, key) => {
      if (key.startsWith('data-')) {
        acc[key] = props[key];
      }

      return acc;
    }, {});

    return {
      type: props.type,
      name: props.name,
      id: props.id,
      disabled: props.disabled,
      autoComplete: props.autoComplete,
      spellCheck: props.spellCheck,
      onBlur: this.handleBlur,
      onFocus: this.handleFocus,
      onChange: this.handleChange,
      onPaste: this.handlePaste,
      onKeyPress: this.handleKeyPress,
      onKeyDown: this.handleKeyDown,
      onClick: this.handleClick,
      //onMouseEnter: this.handleMouseEnter,
      //onMouseLeave: this.handleMouseLeave,
      value: this.format(props.value).value,
      placeholder: props.placeholder,
      maxLength: props.maxLength,
      minLength: props.minLength,
      pattern: props.pattern,
      readOnly: props.readOnly,
      required: props.required,
      tabIndex: props.tabIndex,
      autoFocus: state.focused,
      ...dataAttributes,
    };
  }

  render() {
    return null;
  }
}

export default Control;
